import {
  Component,
  Input,
  AfterViewInit,
  ViewChild,
  ElementRef,
  OnInit
} from '@angular/core';
import Litepicker from 'litepicker';

import connectRange from 'instantsearch.js/es/connectors/range/connectRange';
import { ActivatedRoute } from '@angular/router';
import { BaseWidget } from '../base-widget';
import { RangeBounds } from '../search.types';

interface RangeState {
  min: number | undefined; // absolute min from index
  max: number | undefined; // absolute max from index
  currentMin: number | undefined; // current refined min
  currentMax: number | undefined; // current refined max
  refine: (bounds: [number, number]) => void;
  canRefine: boolean;
  range: RangeBounds;
}

@Component({
  selector: 'app-date-range-picker',
  templateUrl: './date-range-picker.component.html',
  styleUrls: ['./date-range-picker.component.scss']
})
export class DateRangePickerComponent
  extends BaseWidget<RangeState>
  implements OnInit, AfterViewInit
{
  @ViewChild('datepicker') datepicker: ElementRef;

  @Input() attribute: string | undefined;

  picker: any;
  noRange = false;

  public static parseDate(dateString: string) {
    const day = dateString.substring(0, 2);
    const month = dateString.substring(2, 4);
    const year = dateString.substring(4, 8);
    const date = new Date(`${year}-${month}-${day}`);

    return !isNaN(date.getTime()) ? date : undefined;
  }

  createWidget() {
    return connectRange(renderState => {
      const [currentMin, currentMax] = renderState.range
        ? [renderState.range.min, renderState.range.max]
        : [undefined, undefined];

      const [refinedMin, refinedMax] = renderState.start
        ? [
            renderState.start[0] === -Infinity
              ? undefined
              : renderState.start[0],
            renderState.start[1] === Infinity ? undefined : renderState.start[1]
          ]
        : [undefined, undefined];

      // sync inputs only when not actively editing
      // if (this.inputMin === undefined) this.inputMin = refinedMin;
      // if (this.inputMax === undefined) this.inputMax = refinedMax;

      this.updateState({
        min: currentMin,
        max: currentMax,
        currentMin: refinedMin,
        currentMax: refinedMax,
        refine: renderState.refine,
        canRefine: renderState.canRefine,
        range: renderState.range
      });

      const interval = setInterval(() => {
        if (renderState && renderState.range) {
          clearInterval(interval);
          this.picker = new Litepicker({
            element: this.datepicker['nativeElement'],
            singleMode: false,
            resetButton: () => {
              let btn = document.createElement('button');
              btn.innerText = 'Clear';
              return btn;
            },
            setup: picker => {
              picker.on('clear:selection', () => {
                this.onDateChange(undefined, undefined);
              });

              picker.on('selected', (start: Date, end: Date) => {
                this.onDateChange(start.getTime(), end.getTime());
              });
            }
          });

          this._updatePickerStatus();

          const attParam = this.route.snapshot.queryParams[this.attribute];
          if (attParam) {
            const rangeValues = attParam.split(':');
            const startDate = DateRangePickerComponent.parseDate(
              rangeValues[0]
            );
            const endDate = DateRangePickerComponent.parseDate(rangeValues[1]);

            this.picker.setDateRange(startDate, endDate);
          }

          // this.instantSearchInstance.change.subscribe({
          //   next: (event: any) => {
          //     if (
          //       !this.instantSearchInstance.instantSearchInstance.renderState[
          //         ALGOLIA_INDEX
          //       ].clearRefinements.canRefine
          //     ) {
          //       this.reset();
          //     }

          //     this._updatePickerStatus();
          //   }
          // });
        }
      }, 500);
    })({
      attribute: this.attribute
      // ...(this.min !== undefined && { min: this.min }),
      // ...(this.max !== undefined && { max: this.max }),
      // precision: this.precision,
    });
  }

  constructor(private route: ActivatedRoute) {
    super();
  }

  ngOnInit() {
    super.ngOnInit();
    this.algoliaService.instance.on('render', this.onRender);
  }

  ngAfterViewInit() {}

  onDateChange(start: number, end: number) {
    this.state.refine([start, end]);
  }

  private refineFn?: () => void;

  private onRender = () => {
    const helper = this.algoliaService.instance.helper;
    if (!helper || !this.refineFn) return;

    // re-evaluate canRefine after every render
    const hasRefinements = this.hasActiveRefinements();

    if (!hasRefinements) {
      this.reset();
    }
  };

  private hasActiveRefinements(): boolean {
    const helper = this.algoliaService.instance.helper;
    if (!helper) return false;

    const state = helper.state;

    // check facets
    const hasFacets =
      Object.values(state.facetsRefinements ?? {}).some(v => v.length > 0) ||
      Object.values(state.disjunctiveFacetsRefinements ?? {}).some(
        v => v.length > 0
      ) ||
      Object.values(state.hierarchicalFacetsRefinements ?? {}).some(
        v => v.length > 0
      );

    // check numeric filters
    const hasNumeric = Object.values(state.numericRefinements ?? {}).some(v =>
      Object.values(v).some(op => op.length > 0)
    );

    // check tag filters
    const hasTags = (state.tagRefinements ?? []).length > 0;

    // // check query (unless excluded)
    // const hasQuery =
    //   !this.excludedAttributes.includes('query') &&
    //   (state.query ?? '').length > 0;

    // return hasFacets || hasNumeric || hasTags || hasQuery;
    return hasFacets || hasNumeric || hasTags;
  }

  reset() {
    if (this.picker) {
      this.picker.clearSelection();
    }
  }

  showDatePicker() {
    this.picker.show();
  }

  private _updatePickerStatus() {
    const rangeMax = this.state.range.max;

    if (rangeMax == 0) {
      this.noRange = true;
      return;
    }

    const maxDate = this._getMaxDate(rangeMax);
    const minYear = new Date(this.state.range.min).getFullYear();

    this.picker.setOptions({
      minDate: this.state.range.min,
      maxDate: maxDate,
      dropdowns: {
        minYear: minYear,
        maxYear: maxDate.getFullYear(),
        months: true,
        years: true
      }
    });

    this.picker.gotoDate(maxDate);
    this.noRange = false;
  }

  private _getMaxDate(rangeMaxDate: number): Date {
    const current_date = new Date();
    if (rangeMaxDate > current_date.getTime()) {
      return current_date;
    }
    return new Date(rangeMaxDate);
  }
}
